import type {
  CheckPassphraseResponse,
  EditInitResponse,
  GameMeta,
  InitGameRequest,
  InitGameResponse,
  ScoreEntry,
  ScoreListResponse,
  SlugAvailability,
  UploadSlot,
} from '@photo-merge/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    const message =
      detail && typeof detail === 'object' && 'message' in detail
        ? String(detail.message)
        : `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

export function checkSlugAvailability(
  slug: string,
  signal?: AbortSignal,
): Promise<SlugAvailability> {
  return request<SlugAvailability>(`/api/games/${encodeURIComponent(slug)}/available`, { signal });
}

export function initGame(input: InitGameRequest): Promise<InitGameResponse> {
  return request<InitGameResponse>('/api/games/init', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Sends one ball image straight to storage.
 *
 * Deliberately not through the API: in production this URL is a presigned S3 PUT, so
 * image bytes never pass through Lambda. No JSON content-type here — the signature
 * covers the exact content-type the API specified.
 */
export async function uploadBall(slot: UploadSlot, blob: Blob): Promise<void> {
  const res = await fetch(slot.url, {
    method: 'PUT',
    headers: { 'content-type': slot.contentType },
    body: blob,
  });
  if (!res.ok) throw new ApiError(`Upload failed for photo ${slot.index + 1}`, res.status);
}

export function finalizeGame(slug: string, token: string): Promise<GameMeta> {
  return request<GameMeta>(`/api/games/${encodeURIComponent(slug)}/finalize`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export function fetchGame(slug: string, signal?: AbortSignal): Promise<GameMeta> {
  return request<GameMeta>(`/api/games/${encodeURIComponent(slug)}`, { signal });
}

export async function fetchScores(slug: string, signal?: AbortSignal): Promise<ScoreEntry[]> {
  const data = await request<ScoreListResponse>(
    `/api/games/${encodeURIComponent(slug)}/scores`,
    { signal },
  );
  return data.scores;
}

/**
 * Tests a passphrase without changing anything, to unlock the edit screen.
 *
 * The passphrase is checked here rather than in the browser because the browser is
 * never told what it is — a saved game only reports whether one exists.
 */
export function checkEditPassphrase(slug: string, passphrase: string): Promise<CheckPassphraseResponse> {
  return request<CheckPassphraseResponse>(`/api/games/${encodeURIComponent(slug)}/edit/check`, {
    method: 'POST',
    body: JSON.stringify({ passphrase }),
  });
}

/** Claims upload slots for the photos an edit adds. `newBallCount` may be zero. */
export function initEdit(
  slug: string,
  passphrase: string,
  newBallCount: number,
): Promise<EditInitResponse> {
  return request<EditInitResponse>(`/api/games/${encodeURIComponent(slug)}/edit/init`, {
    method: 'POST',
    body: JSON.stringify({ passphrase, newBallCount }),
  });
}

/** Swaps in the finished roster: kept keys and freshly uploaded ones, in play order. */
export function saveEdit(slug: string, passphrase: string, balls: string[]): Promise<GameMeta> {
  return request<GameMeta>(`/api/games/${encodeURIComponent(slug)}/edit/save`, {
    method: 'POST',
    body: JSON.stringify({ passphrase, balls }),
  });
}

export async function submitScore(
  slug: string,
  name: string,
  score: number,
): Promise<ScoreEntry[]> {
  const data = await request<ScoreListResponse>(
    `/api/games/${encodeURIComponent(slug)}/scores`,
    { method: 'POST', body: JSON.stringify({ name, score }) },
  );
  return data.scores;
}
